import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from './HomeNavBar';
import LeftSidebar from './LeftSidebar';
import { LiveStreamCard } from './StreamCard';
import { ROUTES } from '../../constants';
import {
  authApi,
  clearStoredAuth,
  mergeStoredUser,
  readStoredToken,
  readStoredUser,
} from '../../services/authApi';
import streamApi from '../../services/streamApi';
import './HomePage.css';

const LIVE_STREAMS_POLL_MS = 30000;

/**
 * Home Dashboard page — the core experience of Polar Ace.
 *
 * This is where users land after completing the auth flow.
 * Features:
 * - Three-column layout (sidebar, main content, spades panel)
 * - Stream discovery cards
 * The page is fully scrollable with the sidebars fixed in position.
 *
 * @returns {JSX.Element}
 */
function HomePage() {
  const navigate = useNavigate();

  /* ──────────────────────────────────────────────────────────────────────────
   * User State
   * Loads from localStorage if available, falls back to defaults
   * ────────────────────────────────────────────────────────────────────────── */
  const storedUser = readStoredUser();

  const [user, setUser] = useState({
    name: storedUser?.username || storedUser?.firstName || 'StreamerPro',
    avatar: storedUser?.profileImage || null,
    spades: storedUser?.spadesBalance || 0,
    notifications: 999,
  });

  const [liveStreams, setLiveStreams] = useState([]);
  const [liveStreamsLoading, setLiveStreamsLoading] = useState(true);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) return;

    let cancelled = false;

    const syncCurrentUser = async () => {
      try {
        const [userResponse, balanceResponse] = await Promise.all([
          authApi.getCurrentUser(token).catch(() => null),
          authApi.getPaymentBalance(undefined, token).catch(() => null),
        ]);

        if (cancelled) return;

        const currentUser = userResponse?.user;
        if (currentUser) {
          mergeStoredUser(currentUser);
        }

        setUser((prev) => ({
          ...prev,
          name: currentUser?.username || currentUser?.firstName || prev.name,
          avatar: currentUser?.profileImage || prev.avatar,
          spades: typeof balanceResponse?.spade_balance === 'number'
            ? balanceResponse.spade_balance
            : prev.spades,
        }));
      } catch (error) {
        console.error('Failed to sync current user for home page:', error);
      }
    };

    syncCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadLiveStreams = async () => {
      try {
        const streams = await streamApi.getLiveStreams();
        if (cancelled) return;
        setLiveStreams(Array.isArray(streams) ? streams : []);
      } catch (error) {
        console.warn('Failed to load live streams:', error);
        if (!cancelled) setLiveStreams([]);
      } finally {
        if (!cancelled) setLiveStreamsLoading(false);
      }
    };

    loadLiveStreams();
    const intervalId = setInterval(loadLiveStreams, LIVE_STREAMS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────────────────
   * Handlers
   * ────────────────────────────────────────────────────────────────────────── */
  const handleSpadesClick = useCallback(() => {}, []);

  const handleSpadesPurchaseComplete = useCallback((result) => {
    const nextBalance = result?.new_balance ?? result?.spade_balance;
    if (typeof nextBalance !== 'number') {
      return;
    }

    setUser((prev) => ({
      ...prev,
      spades: nextBalance,
    }));
  }, []);

  const handleLogout = useCallback(async () => {
    const token = readStoredToken();
    await authApi.logout(token);
    clearStoredAuth();
    setUser({
      name: '',
      avatar: null,
      spades: 0,
      notifications: 0,
    });
    navigate(ROUTES.WELCOME);
  }, [navigate]);

  /* ──────────────────────────────────────────────────────────────────────────
   * Render
   * ────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="home-page">
      {/* Navigation */}
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={handleSpadesClick}
        onSpadesPurchaseComplete={handleSpadesPurchaseComplete}
        onLogout={handleLogout}
      />

      {/* Left Sidebar */}
      <LeftSidebar />

      {/* Main Content */}
      <main className="home-page__main">
        <div className="home-page__content">

          {/* Live Streams Row */}
          {liveStreams.length > 0 && (
            <section className="home-page__cards">
              {liveStreams.map((stream) => (
                <LiveStreamCard key={stream.userId} stream={stream} />
              ))}
            </section>
          )}

          {liveStreams.length === 0 && !liveStreamsLoading && (
            <section className="home-page__empty">
              <p>No streamers are live right now.</p>
            </section>
          )}
        </div>

        {/* Footer */}
        <Footer />
      </main>
    </div>
  );
}

export default HomePage;
